"use client";

import { ReloadIcon } from "@radix-ui/react-icons";
import { useStytchB2BClient } from "@stytch/nextjs/b2b";
import Image from "next/image";
import { useState } from "react";

import Logo from "@/src/components/logo";
import { Button } from "@/src/components/shadcn/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/src/components/shadcn/ui/card";
import { Input } from "@/src/components/shadcn/ui/input";
import { useToast } from "@/src/hooks/use-toast";
import { logger } from "@/src/lib/logging/logger";
import { isServer } from "@/src/lib/runtime";
import { emailSchema } from "@/src/models/common-schema";

const getLoginRedirectUrl = (redirectPath?: string) => {
  const isLocalhost = !isServer && window.location.hostname === "localhost";
  const baseUrl = isLocalhost ? process.env.NEXT_PUBLIC_APP_URL! : "https://dashboard.valeriehealth.com";

  const url = new URL("/authenticate", baseUrl);

  if (redirectPath) {
    logger.info("Setting redirect path", { redirectPath });

    url.searchParams.set("redirect", redirectPath);
  }

  return url.toString();
};

const LoginContainer = ({
  orgId,
  redirectUrl,
  errorMessage,
}: {
  orgId: string;
  redirectUrl?: string;
  errorMessage?: string;
}) => {
  const [otpConfirmation, setOtpConfirmation] = useState(false);
  const [email, setEmail] = useState("");

  return (
    <div className="flex items-center justify-center min-h-screen">
      {otpConfirmation ? (
        <OtpConfirmCard email={email} setOtpConfirmation={setOtpConfirmation} />
      ) : (
        <EmailInputCard
          email={email}
          setEmail={setEmail}
          orgId={orgId}
          setOtpConfirmation={setOtpConfirmation}
          redirectUrl={redirectUrl}
          errorMessage={errorMessage}
        />
      )}
    </div>
  );
};

export default LoginContainer;

const EmailInputCard = ({
  email,
  setEmail,
  orgId,
  redirectUrl,
  errorMessage,
  setOtpConfirmation,
}: {
  email: string;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  orgId: string;
  redirectUrl?: string;
  errorMessage?: string;
  setOtpConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [hasBlurred, setHasBlurred] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const validateEmail = (emailValue: string) => {
    const result = emailSchema.safeParse(emailValue);
    const isValid = result.success;

    setIsEmailValid(isValid);

    return isValid;
  };

  const getEmailError = () => {
    if (!hasBlurred || isEmailValid) {
      return null;
    }

    const result = emailSchema.safeParse(email);

    return result.success ? null : result.error.issues[0].message;
  };

  const authenticateUrl = getLoginRedirectUrl(redirectUrl);

  const stytchClient = useStytchB2BClient();
  const { toast } = useToast();

  const handleGoogleLogin = async () => {
    stytchClient.oauth.google.start({
      organization_id: orgId,
      login_redirect_url: authenticateUrl,
    });
  };

  const handleLogin = async (e: React.MouseEvent<HTMLButtonElement> | React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setHasBlurred(true);
    const isValidEmail = validateEmail(email);

    if (!isValidEmail) {
      return;
    }

    setIsLoading(true);

    const loginResp = await stytchClient.magicLinks.email.loginOrSignup({
      email_address: email,
      organization_id: orgId,
      login_redirect_url: authenticateUrl,
    });

    if (loginResp.status_code !== 200) {
      toast({
        title: "Something went wrong.",
        description: "Please try again.",
        variant: "destructive",
      });
    } else {
      setOtpConfirmation(true);
    }

    setIsLoading(false);
  };

  return (
    <Card className="w-full max-w-lg">
      <CardHeader className="flex flex-col items-center gap-2">
        <Logo size="large" />
        <CardTitle className="text-2xl">Sign in to Valerie Health</CardTitle>
        {errorMessage && <CardDescription className="text-center text-destructive">{errorMessage}</CardDescription>}
        <CardDescription className="text-muted-foreground">Please enter your email address.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col">
        <form id="loginForm" onSubmit={handleLogin}>
          <Input
            className="p-6 bg-input"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => {
              const newValue = e.target.value;

              setEmail(newValue);

              validateEmail(newValue);

              // Clear the blurred state when user starts typing to hide errors
              if (hasBlurred) {
                setHasBlurred(false);
              }
            }}
            onBlur={(e) => {
              setHasBlurred(true);

              validateEmail(e.target.value);
            }}
          />
        </form>
        {getEmailError() && <span className="text-destructive text-center">{getEmailError()}</span>}
      </CardContent>
      <CardFooter className="flex flex-col gap-6 justify-center w-full">
        <Button type="submit" className="w-full p-6" form="loginForm" disabled={isLoading || !isEmailValid}>
          {isLoading ? <ReloadIcon className="animate-spin" /> : "Sign in"}
        </Button>
        <Button className="w-full p-6 flex items-center gap-2" variant="outline" onClick={handleGoogleLogin}>
          <Image src="/google.svg" alt="Google" width={24} height={24} />
          Sign in with Google
        </Button>
      </CardFooter>
    </Card>
  );
};

const OtpConfirmCard = ({
  email,
  setOtpConfirmation,
}: {
  email: string;
  setOtpConfirmation: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  return (
    <Card className="w-full max-w-lg">
      <CardHeader className="flex flex-col items-center gap-2">
        <Logo size="large" />
        <CardTitle className="text-2xl">Check your email</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-2">
        <CardDescription className="text-muted-foreground">
          An email was sent to <strong>{email}</strong>.
        </CardDescription>
        <Button variant="link" onClick={() => setOtpConfirmation(false)}>
          Send another
        </Button>
      </CardContent>
    </Card>
  );
};
